import SvgIcon from "@/components/common/SvgIcon";

import capitlizeText from "@/utilize/capitlizeText";
import { convertDate } from "@/utilize/convertDate";
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from "@material-tailwind/react";
import classNames from "classnames";
import {
  Children,
  cloneElement,
  Dispatch,
  isValidElement,
  ReactNode,
  SetStateAction,
  useState,
} from "react";
import Button from "../Button";
import Checkbox from "../Checkbox";
import Radio from "../Radio";
import SelectBox from "../Selectbox";
import TextFieldDate from "../TextFieldDate";
import TextFieldRangeDatePicker from "../TextFieldRangeDatePicker";
import Typography from "../Typography";

function isEmptyObjectOrArray(value: any) {
  if (Array.isArray(value)) {
    return value.length === 0; // []
  } else if (typeof value === "object" && value !== null) {
    return Object.keys(value).length === 0; // {}
  }
  return false;
}

type FilterProps<T> = {
  children: ReactNode;
  onFiltersChange: (filters: Partial<T>) => void;
  groupFilterChange?: (values: T) => void;
  values?: Partial<T>;
  className?: string;
  onClearFilter: () => void;
};

type FilterContextProps<T> = {
  onFilterChange?: (key: keyof T, value: any) => void;
  groupFilterChange?: (values: any) => void;
  currentFilters?: Partial<T>;
  className?: string;
};

function FilterRoot<T>({
  children,
  onFiltersChange,
  values = {},
  className,
  onClearFilter,
}: FilterProps<T>) {
  const handleChange = (key: keyof T, value: any) => {
    const updated = typeof value === "function" ? value(values[key]) : value;
    const newFilters = { ...values, [key]: updated };
    onFiltersChange(newFilters);
  };

  const handleGroup = (vals: any) => {
    const newFilters = { ...values, ...vals };
    onFiltersChange(newFilters);
  };

  const sharedProps: FilterContextProps<T> = {
    onFilterChange: handleChange,
    groupFilterChange: handleGroup,
    currentFilters: values,
  };
  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { ...sharedProps } as any);
    }
    return child;
  });

  let isClearFilterVisible = false;

  Children.forEach(children, (item: any) => {
    if (item?.type) {
      const componentName = item?.type.name;
      let keyName = item?.props?.keyName;

      if (!keyName && componentName === "FilterRangePicker") {
        keyName = item?.props?.startDatekeyName;
      }
      if (
        componentName !== "FilterSort" &&
        keyName &&
        values &&
        keyName in values &&
        values[keyName as keyof typeof values]
      ) {
        if (!isEmptyObjectOrArray(values[keyName as keyof typeof values])) {
          isClearFilterVisible = true;
        }
      }
    }
  });

  return (
    <div
      className={classNames(
        "flex gap-x-2 items-center justify-between",
        className,
      )}
    >
      <div className="flex gap-x-2 flex-nowrap overflow-auto pb-[2px]">
        {enhancedChildren}
      </div>
      {onClearFilter && isClearFilterVisible && (
        <Button variant="text" size="xs" onClick={onClearFilter}>
          <SvgIcon iconName="close" />
          <Typography variant="Caption1-Bold">Clear filter</Typography>
        </Button>
      )}
    </div>
  );
}

type Option = { value: any; label: string };

type SortProps<T> = {
  label: string;
  options: Option[];
  keyName: keyof T;
  dataToShow?: ReactNode;
} & FilterContextProps<T>;

function FilterSort<T>({
  options,
  onFilterChange,
  dataToShow,
  keyName,
  label,
  currentFilters,
}: SortProps<T>) {
  const [selectedItem, setSelectedItem] = useState<string>(
    (currentFilters?.[keyName] || "") as string,
  );
  const [open, setOpen] = useState(false);
  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      icon={<SvgIcon iconName="sortFromBottomToTop" />}
      title={label}
      dataToShow={dataToShow}
      onClose={() =>
        setSelectedItem((currentFilters?.[keyName] || "") as string)
      }
      applyElement={
        <Button
          onClick={() => {
            onFilterChange?.(keyName, selectedItem);
            setOpen(false);
          }}
          className="w-full"
          size="sm"
        >
          Apply
        </Button>
      }
    >
      {options?.map((item) => (
        <div
          className="flex items-center gap-x-2 mb-2 py-2 cursor-pointer"
          key={item?.value}
          onClick={() => {
            setSelectedItem(item?.value);
          }}
        >
          <Radio
            checked={selectedItem === item.value}
            value={item?.value}
            id={item.value}
          />
          <Typography block variant="Body2-Regular">
            {item?.label}
          </Typography>
        </div>
      ))}
    </FilterButton>
  );
}

type CheckboxGroupProps<T> = {
  label: string;
  options: Option[];
  keyName: keyof T;
  dataToShow?: ReactNode;
  dataToShowArray?: string[];
} & FilterContextProps<T>;

function FilterCheckboxGroup<T>({
  label,
  options,
  keyName,
  onFilterChange,
  currentFilters,
  dataToShow,
  dataToShowArray,
}: CheckboxGroupProps<T>) {
  const [selected, setSelected] = useState(
    new Set((currentFilters?.[keyName as keyof T] as string[]) || []),
  );
  const [open, setOpen] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    const newSet = new Set(selected);
    checked ? newSet.add(value) : newSet.delete(value);
    setSelected(new Set([...newSet]));
  };

  const onApply = () => {
    const checked: string[] = [];
    selected?.forEach((item) => checked.push(item));
    onFilterChange?.(keyName, checked);
  };

  const [firstFilter, secondFilter, ...rest] = dataToShowArray || [];
  const arrayDataShow =
    `${firstFilter || ""} ${secondFilter ? `, ${secondFilter}` : ``} ${rest?.length ? `+ ${rest?.length}` : ""}`.trim();
  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      title={label}
      dataToShow={dataToShow || arrayDataShow}
      onClear={() => {
        onFilterChange?.(keyName, []);
      }}
      onClose={() =>
        setSelected(
          new Set((currentFilters?.[keyName as keyof T] as string[]) || ""),
        )
      }
      applyElement={
        <Button
          onClick={() => {
            onApply();
            setOpen(false);
          }}
          className="w-full"
          size="xs"
        >
          Apply
        </Button>
      }
    >
      <div className="max-h-40 mb-2 overflow-y-auto">
        {options?.map((item) => (
          <div className="flex items-center gap-x-2 py-2" key={item?.value}>
            <Checkbox
              checked={selected.has(item.value)}
              value={item?.value}
              id={item.value}
              onChange={handleChange}
              className="w-full"
              label={
                <Typography block variant="Body2-Regular">
                  {item?.label}
                </Typography>
              }
            />
          </div>
        ))}
      </div>
    </FilterButton>
  );
}

type RadioBoxGroupProps<T> = {
  label: string;
  options: Option[];
  keyName: keyof T;
  dataToShow?: ReactNode;
  dataToShowArray?: string;
} & FilterContextProps<T>;

function FilterRadioboxGroup<T>({
  label,
  options,
  keyName,
  onFilterChange,
  currentFilters,
  dataToShow,
  dataToShowArray,
}: RadioBoxGroupProps<T>) {
  const [selected, setSelected] = useState(
    currentFilters?.[keyName as keyof T] as string,
  );
  const [open, setOpen] = useState(false);

  const [firstFilter, secondFilter, ...rest] = dataToShowArray || [];
  const arrayDataShow =
    `${capitlizeText(firstFilter) || ""} ${capitlizeText(secondFilter) || ""} ${rest?.length ? `+ ${rest?.length}` : ""}`.trim();
  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      title={label}
      dataToShow={dataToShow || arrayDataShow}
      onClear={() => {
        onFilterChange?.(keyName, "");
        setSelected("");
      }}
      applyElement={
        <Button
          onClick={() => {
            onFilterChange?.(keyName, selected);
            setOpen(false);
          }}
          className="w-full"
          size="sm"
        >
          Apply
        </Button>
      }
    >
      {options?.map((item) => (
        <div
          className="flex items-center cursor-pointer gap-x-2 mb-2 py-2"
          onClick={() => setSelected(item?.value)}
          key={item?.value}
        >
          <Radio
            checked={selected === item?.value}
            value={item?.value}
            id={item.value}
            className="w-full"
          />
          <Typography block variant="Body2-Regular">
            {item?.label}
          </Typography>
        </div>
      ))}
    </FilterButton>
  );
}

type FilterDropdownProps<T> = {
  label: string;
  options: Option[];
  keyName: keyof T;
  placeholder?: string;
  dataToShow?: ReactNode;
} & FilterContextProps<T>;

function FilterDropdownFilter<T>({
  label,
  options,
  keyName,
  onFilterChange,
  currentFilters,
  placeholder,
  dataToShow,
}: FilterDropdownProps<T>) {
  const [selectedItem, setSelectedItem] = useState<string>(
    (currentFilters?.[keyName] || "") as string,
  );
  const [open, setOpen] = useState(false);
  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      title={label}
      dataToShow={dataToShow}
      onClose={() =>
        setSelectedItem((currentFilters?.[keyName] || "") as string)
      }
      onClear={() => onFilterChange?.(keyName, "")}
      applyElement={
        <Button
          onClick={() => {
            onFilterChange?.(keyName, selectedItem);
            setOpen(false);
          }}
          className="w-full"
          size="sm"
        >
          Apply
        </Button>
      }
    >
      <SelectBox<ArrayElement<typeof options>>
        className={`!h-10 mb-2 min-w-[180px]`}
        value={selectedItem}
        onChange={(value) => setSelectedItem(value)}
        options={options || []}
        placeholder={placeholder}
        renderOption={(option) => (
          <Typography variant="Body2-Regular">{option.label}</Typography>
        )}
        renderValue={(option) => option?.label}
      />
    </FilterButton>
  );
}

type DatePickerFilterProps<T> = {
  label: string;
  keyName: keyof T;
  placeholder?: string;
  dataToShow?: ReactNode;
  untilDate?: Date;
  fromDate?: Date;
} & FilterContextProps<T>;

function FilterDatePicker<T>({
  label,
  keyName,
  onFilterChange,
  currentFilters,
  dataToShow,
  fromDate,
  untilDate,
}: DatePickerFilterProps<T>) {
  const [value, setValue] = useState<string>(
    (currentFilters?.[keyName] || "") as string,
  );
  const [open, setOpen] = useState(false);
  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      title={label}
      dataToShow={dataToShow}
      onClose={() => setValue((currentFilters?.[keyName] || "") as string)}
      onClear={() => onFilterChange?.(keyName, "")}
      applyElement={
        <Button
          onClick={() => {
            onFilterChange?.(keyName, value);
            setOpen(false);
          }}
          className="w-full"
          size="sm"
        >
          Apply
        </Button>
      }
    >
      <div className="mb-2">
        <TextFieldDate
          minimumDate={fromDate && dateToObject(fromDate)}
          maximumDate={untilDate && dateToObject(untilDate)}
          value={value}
          placeholder={label}
          className="!h-10"
          onChange={(value) => setValue(value)}
        />
      </div>
    </FilterButton>
  );
}
function dateToObject(date: Date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // getMonth() devuelve de 0 (enero) a 11 (diciembre)
    day: date.getDate(),
  };
}

type RangePickerFilterProps<T> = {
  label: string;
  startDatekeyName: keyof T;
  endDateKeyName: keyof T;
  placeholder?: string;
  dataToShow?: ReactNode;
} & FilterContextProps<T>;

type RangeValue = {
  from: string;
  to: string;
};

function FilterRangePicker<T>({
  label,
  startDatekeyName,
  endDateKeyName,
  currentFilters,
  dataToShow,
  placeholder,
  groupFilterChange,
}: RangePickerFilterProps<T>) {
  const [value, setValue] = useState<RangeValue>({
    from: (currentFilters?.[startDatekeyName] || "") as string,
    to: (currentFilters?.[endDateKeyName] || "") as string,
  });
  const [open, setOpen] = useState(false);

  const startDate = currentFilters?.[startDatekeyName]
    ? convertDate(new Date(currentFilters?.[startDatekeyName] as string))
    : "";
  const endDate = currentFilters?.[endDateKeyName]
    ? convertDate(new Date(currentFilters?.[endDateKeyName] as string))
    : "";

  const defaultDataToShow =
    `${startDate} ${endDate ? `- ${endDate}` : ""}`.trim();

  const handleApply = () => {
    groupFilterChange?.({
      [startDatekeyName]: value.from,
      [endDateKeyName]: value.to,
    });
    setOpen(false);
  };

  const handleClear = () => {
    groupFilterChange?.({ [startDatekeyName]: "", [endDateKeyName]: "" });
    setValue({ from: "", to: "" });
  };

  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      title={label}
      dataToShow={dataToShow || defaultDataToShow}
      onClose={() =>
        setValue({
          from: (currentFilters?.[startDatekeyName] || "") as string,
          to: (currentFilters?.[endDateKeyName] || "") as string,
        })
      }
      onClear={handleClear}
      applyElement={
        <Button onClick={handleApply} className="w-full" size="sm">
          Apply
        </Button>
      }
    >
      <div className="mb-2">
        <TextFieldRangeDatePicker
          value={value}
          onChange={(val) => {
            setValue(val as RangeValue);
          }}
          className="!h-10"
          icon={<SvgIcon iconName="calendar" />}
          placeholder={placeholder}
        />
      </div>
    </FilterButton>
  );
}

type FilterElementProps<T> = {
  children: (data: { value?: any; setValue: (data: any) => void }) => ReactNode;
  keyName: keyof T;
  dataToShow?: ReactNode;
  label: string;
  onApply?: (key: keyof T, value: any) => void;
  onClear?: () => void;
  dataToShowArray?: string[];
} & FilterContextProps<T>;

function FilterElement<T>({
  dataToShow,
  label,
  currentFilters,
  children,
  onFilterChange,
  keyName,
  onApply,
  onClear,
  dataToShowArray,
}: FilterElementProps<T>) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>(
    (currentFilters?.[keyName] || "") as string,
  );

  const [firstFilter, secondFilter, ...rest] = dataToShowArray || [];
  const arrayDataShow =
    `${firstFilter || ""} ${secondFilter ? `, ${secondFilter}` : ``} ${rest?.length ? `+ ${rest?.length}` : ""}`.trim();

  return (
    <FilterButton
      open={open}
      setOpen={setOpen}
      title={label}
      dataToShow={dataToShow || arrayDataShow}
      onClear={() => {
        if (onClear) {
          onClear();
        } else {
          onFilterChange?.(keyName, "");
        }
      }}
      onClose={() => setValue((currentFilters?.[keyName] || "") as string)}
      applyElement={
        <Button
          onClick={() => {
            if (onApply) {
              onApply(keyName, value);
            } else {
              onFilterChange?.(keyName, value);
            }
            setOpen(false);
          }}
          className="w-full mt-2"
          size="sm"
        >
          Apply
        </Button>
      }
    >
      {children({ value, setValue })}
    </FilterButton>
  );
}

const FilterButton = ({
  dataToShow,
  icon,
  title,
  popupTitle,
  children,
  onClose,
  applyElement,
  open,
  setOpen,
  onClear,
}: {
  icon?: ReactNode;
  dataToShow?: ReactNode;
  title?: string;
  popupTitle?: string;
  children: ReactNode;
  onClose?: () => void;
  applyElement: ReactNode;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onClear?: () => void;
}) => {
  return (
    <div className="w-fit cursor-pointer text-nowrap !z-[20]">
      <Popover
        open={open}
        handler={() => {
          setOpen((p) => !p);
          onClose?.();
        }}
        placement="bottom"
      >
        <PopoverHandler>
          <div
            className={classNames(
              "border hover:bg-gray-100 transition-all border-gray-200 py-1 px-2 rounded",
              {
                "!border-dashed": !dataToShow,
              },
            )}
          >
            <div className="flex justify-between gap-x-2">
              <div className="flex gap-x-1 items-center">
                <div>
                  {icon ? (
                    icon
                  ) : (
                    <SvgIcon
                      onClick={(e) => {
                        if (dataToShow) {
                          e.stopPropagation();
                          onClear?.();
                        }
                      }}
                      iconName="closeCircleFill"
                      className={classNames(
                        "cursor-pointer w-5 h-5 transition-all",
                        {
                          "rotate-45": !dataToShow,
                        },
                      )}
                    />
                  )}
                </div>
                <Typography variant="BodyPlus3-Regular">{title}</Typography>
              </div>
              {!!dataToShow && (
                <>
                  <div className="border border-gray-100" />
                  <div className="flex gap-x-1 items-center">
                    <Typography
                      variant="BodyPlus3-Regular"
                      className="!text-blue-700"
                    >
                      {dataToShow}
                    </Typography>
                    <SvgIcon iconName="arrowDown" />
                  </div>
                </>
              )}
            </div>
          </div>
        </PopoverHandler>

        <PopoverContent placeholder="" className="min-w-[180px] z-[9999]">
          <div className="flex flex-col gap-y-3">
            <Typography block variant="Body1-Bold">
              {popupTitle || title}
            </Typography>
            <div>{children}</div>
          </div>
          <div>{applyElement}</div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const filterComponents = {
  FilterDropdownFilter,
  FilterCheckboxGroup,
  FilterSort,
  FilterDatePicker,
  FilterElement,
  FilterRangePicker,
  FilterRadioboxGroup,
};

const Filter = Object.assign(FilterRoot, filterComponents);

export default Filter;
